/**
 * MobileAddMissionModal
 *
 * MUI full-screen Dialog replacement for AddMissionModal (PrimeReact Dialog).
 * Resolves z-index issues when opened inside MobileCollabModalDetails SwipeableDrawer.
 *
 * Components from MUI: Dialog, AppBar, Toolbar, IconButton, Typography, Autocomplete,
 *   TextField, Button, Box
 */
import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import moment from "moment";

// MUI Components
import {
  Dialog,
  AppBar,
  Toolbar,
  IconButton,
  Typography,
  Autocomplete,
  TextField,
  Button,
  Box,
  Slide,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";

// Redux
import {
  addCollabMission,
  getCollabMissions,
  getTeams,
} from "../../../components/teams/teamsSlice";
import { getPostes } from "../../../components/collabs/collabsSlice";

// Shared utilities
import Langs from "../../../app/lang/langs";
import { showErrorToast } from "../../../app/utils/helpers";

/* ------------------------------------------------------------------ */
/*  Shared style constants                                             */
/* ------------------------------------------------------------------ */
const TRANSITION_SMOOTH = "all 0.25s cubic-bezier(0.4, 0, 0.2, 1)";
const BORDER_RADIUS_CARD = "16px";
const BORDER_RADIUS_INPUT = "12px";
const BORDER_RADIUS_BUTTON = "14px";

const fieldLabelSx = {
  fontWeight: 600,
  mb: 0.75,
  letterSpacing: "-0.01em",
  color: "text.secondary",
  fontSize: "0.82rem",
  textTransform: "uppercase",
};

const glassmorphicTextFieldSx = {
  mb: 3,
  "& .MuiOutlinedInput-root": {
    borderRadius: BORDER_RADIUS_INPUT,
    backgroundColor: "rgba(255,255,255,0.06)",
    backdropFilter: "blur(6px)",
    transition: TRANSITION_SMOOTH,
    "& fieldset": {
      borderColor: "rgba(0,0,0,0.08)",
      transition: TRANSITION_SMOOTH,
    },
    "&:hover fieldset": {
      borderColor: "rgba(0,0,0,0.18)",
    },
    "&.Mui-focused fieldset": {
      borderColor: "rgba(255,144,21,0.5)",
      boxShadow: "0 0 0 3px rgba(255,144,21,0.12)",
    },
    "&.Mui-focused": {
      backgroundColor: "rgba(255,255,255,0.10)",
    },
  },
  "& input": {
    fontSize: "16px",
    transition: TRANSITION_SMOOTH,
  },
  "& input::placeholder": {
    opacity: 0.45,
  },
};

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

export default function MobileAddMissionModal(props) {
  const dispatch = useDispatch();
  const postes = useSelector((state) => state.collabs.postes);
  const teams = useSelector((state) => state.teams.allTeams);

  const [newMission, setNewMission] = useState({});

  useEffect(() => {
    if (!teams || teams.length === 0) {
      dispatch(getTeams());
    }
    if (!postes || postes.length === 0) {
      dispatch(getPostes());
    }
  }, [dispatch, teams, postes]);

  const handleClose = () => {
    props.onHide();
    setNewMission({});
  };

  const isFormValid = () => {
    return newMission.id_poste && newMission.id_team && newMission.start_date;
  };

  const handleAddMission = async () => {
    try {
      const actionResponse = await dispatch(
        addCollabMission({
          id_collab: props.collabDetails.id,
          id_team: newMission.id_team,
          id_poste: newMission.id_poste,
          start_date: newMission.start_date
            ? moment(newMission.start_date).format("YYYY-MM-DD HH:mm:ss")
            : null,
          end_date: newMission.end_date
            ? moment(newMission.end_date).format("YYYY-MM-DD HH:mm:ss")
            : null,
        })
      ).unwrap();
      if (actionResponse.responseData?.error == "conflict") {
        showErrorToast("MISSION_CONFLICT_ERROR");
      } else if (actionResponse.responseData?.status) {
        dispatch(getCollabMissions({ id_collab: props.collabDetails.id }));
        props.onHide();
        setNewMission({});
      }
    } catch (error) {
      console.error("Error adding mission:", error);
      showErrorToast();
    }
  };

  return (
    <Dialog
      fullScreen
      open={!!props.show}
      onClose={handleClose}
      TransitionComponent={Transition}
      sx={{
        zIndex: 1400,
        "& .MuiDialog-paper": {
          background: "var(--surface-ground, #f4f6f8)",
        },
      }}
    >
      {/* Top AppBar */}
      <AppBar
        sx={{
          position: "sticky",
          top: 0,
          bgcolor: "rgba(255,255,255,0.82)",
          color: "text.primary",
          boxShadow: "0 1px 12px rgba(0,0,0,0.06)",
          backdropFilter: "blur(14px)",
          WebkitBackdropFilter: "blur(14px)",
          borderBottom: "1px solid rgba(0,0,0,0.06)",
        }}
      >
        <Toolbar sx={{ py: 0.5 }}>
          <IconButton
            edge="start"
            onClick={handleClose}
            aria-label="close"
            sx={{
              transition: TRANSITION_SMOOTH,
              borderRadius: "12px",
              "&:hover": {
                backgroundColor: "rgba(0,0,0,0.06)",
                transform: "scale(1.08)",
              },
              "&:active": {
                transform: "scale(0.95)",
              },
            }}
          >
            <CloseIcon />
          </IconButton>
          <Typography
            sx={{
              ml: 1.5,
              flex: 1,
              fontWeight: 600,
              fontSize: "1.15rem",
              letterSpacing: "-0.02em",
            }}
            variant="h6"
          >
            <Langs str="ADD_MISSION" />
          </Typography>
        </Toolbar>
      </AppBar>

      {/* Form Content */}
      <Box
        sx={{
          p: 2.5,
          pb: 14,
          overflowY: "auto",
          flex: 1,
          "@keyframes fadeSlideIn": {
            from: { opacity: 0, transform: "translateY(10px)" },
            to: { opacity: 1, transform: "translateY(0)" },
          },
          animation: "fadeSlideIn 0.35s cubic-bezier(0, 0, 0.2, 1) both",
        }}
      >
        {/* Glassmorphic Card wrapper */}
        <Box
          sx={{
            background: "rgba(255,255,255,0.65)",
            backdropFilter: "blur(16px)",
            WebkitBackdropFilter: "blur(16px)",
            borderRadius: BORDER_RADIUS_CARD,
            border: "1px solid rgba(255,255,255,0.45)",
            boxShadow:
              "0 2px 16px rgba(0,0,0,0.04), 0 0.5px 2px rgba(0,0,0,0.03)",
            p: 2.5,
            transition: TRANSITION_SMOOTH,
          }}
        >
          {/* Client / Team */}
          <Typography variant="body2" sx={fieldLabelSx}>
            <Langs str="CLIENT" />
          </Typography>
          <Autocomplete
            options={teams || []}
            getOptionLabel={(opt) => opt.label || ""}
            isOptionEqualToValue={(opt, val) => opt.value === val.value}
            value={
              (teams || []).find((o) => o.value === newMission.id_team) || null
            }
            onChange={(_, newVal) =>
              setNewMission((prev) => ({
                ...prev,
                id_team: newVal ? newVal.value : null,
              }))
            }
            slotProps={{ popper: { sx: { zIndex: 1500 } } }}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder="Sélectionner un client"
                size="small"
                sx={glassmorphicTextFieldSx}
              />
            )}
            fullWidth
          />

          {/* Poste */}
          <Typography variant="body2" sx={fieldLabelSx}>
            <Langs str="POSTE" />
          </Typography>
          <Autocomplete
            options={postes || []}
            getOptionLabel={(opt) => opt.label || ""}
            isOptionEqualToValue={(opt, val) => opt.value === val.value}
            value={
              (postes || []).find((o) => o.value === newMission.id_poste) ||
              null
            }
            onChange={(_, newVal) =>
              setNewMission((prev) => ({
                ...prev,
                id_poste: newVal ? newVal.value : null,
              }))
            }
            slotProps={{ popper: { sx: { zIndex: 1500 } } }}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder="Nom du poste"
                size="small"
                sx={glassmorphicTextFieldSx}
              />
            )}
            fullWidth
          />

          {/* Start Date */}
          <Typography variant="body2" sx={fieldLabelSx}>
            <Langs str="START_DATE" />
          </Typography>
          <TextField
            type="date"
            size="small"
            fullWidth
            value={
              newMission.start_date
                ? moment(newMission.start_date).format("YYYY-MM-DD")
                : ""
            }
            onChange={(e) =>
              setNewMission((prev) => ({
                ...prev,
                start_date: e.target.value ? new Date(e.target.value) : null,
              }))
            }
            InputLabelProps={{ shrink: true }}
            sx={glassmorphicTextFieldSx}
          />

          {/* End Date */}
          <Typography variant="body2" sx={fieldLabelSx}>
            <Langs str="END_DATE" />
          </Typography>
          <TextField
            type="date"
            size="small"
            fullWidth
            disabled={!newMission.start_date}
            value={
              newMission.end_date
                ? moment(newMission.end_date).format("YYYY-MM-DD")
                : ""
            }
            inputProps={{
              min: newMission.start_date
                ? moment(newMission.start_date).format("YYYY-MM-DD")
                : undefined,
            }}
            onChange={(e) =>
              setNewMission((prev) => ({
                ...prev,
                end_date: e.target.value ? new Date(e.target.value) : null,
              }))
            }
            InputLabelProps={{ shrink: true }}
            sx={glassmorphicTextFieldSx}
          />
        </Box>
      </Box>

      {/* Sticky bottom button */}
      <Box
        sx={{
          position: "fixed",
          bottom: 0,
          left: 0,
          right: 0,
          p: 2.5,
          bgcolor: "rgba(255,255,255,0.78)",
          backdropFilter: "blur(18px)",
          WebkitBackdropFilter: "blur(18px)",
          boxShadow: "0 -4px 24px rgba(0,0,0,0.06)",
          borderTop: "1px solid rgba(255,255,255,0.5)",
          zIndex: 1,
        }}
      >
        <Button
          variant="contained"
          color="warning"
          fullWidth
          disabled={!isFormValid()}
          onClick={handleAddMission}
          sx={{
            minHeight: 52,
            borderRadius: BORDER_RADIUS_BUTTON,
            textTransform: "none",
            fontWeight: 600,
            fontSize: "1.05rem",
            letterSpacing: "-0.01em",
            boxShadow: "0 4px 14px rgba(255,144,21,0.28)",
            transition: TRANSITION_SMOOTH,
            "&:hover": {
              transform: "scale(1.02)",
              boxShadow: "0 6px 20px rgba(255,144,21,0.35)",
            },
            "&:active": {
              transform: "scale(0.98)",
            },
            "&.Mui-disabled": {
              boxShadow: "none",
            },
          }}
        >
          <Langs str="ADD" />
        </Button>
      </Box>
    </Dialog>
  );
}
